import styled from '@emotion/styled';
import { Link, Stack, Text, tokens } from '@hotjar/hotsauce';
import { useProductCatalogRepo } from '@hotjar/product-catalog';
import { useTranslationWithPrefix } from '@hotjar/translations';
import type { SiteItem } from '../../hooks/useServiceResourceList';
import type { EditableState } from '../product-summary-item/ProductSummaryItem';
import { ProductSummaryItem } from '../product-summary-item/ProductSummaryItem';
import type {
  BillingPeriod,
  ProductAllowance,
} from '~/subscription-management/api/common/model/subscription';
import type { Currency } from '~/subscription-management/api/common/model/customer';
import { Discount } from '~/subscription-management/components/discount/Discount';

interface Props {
  site: SiteItem;
  updatedProducts?: ProductAllowance[];
  billingPeriod: BillingPeriod;
  currency: Currency;
  editable: EditableState;
  subscriptionEndDate?: string;
  onPlanChange?(product: ProductAllowance): void;
  showBundleDiscount: boolean;
}

const S = {
  Container: styled.div`
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacer.x2};
  `,
  Link: styled(Link)`
    ${tokens.truncateText()}
    svg {
      display: none;
    }
  `,
  Stack: styled(Stack)`
    align-items: center;
  `,
};

export function SiteProductsSummary({
  site,
  billingPeriod,
  currency,
  updatedProducts = [],
  editable,
  subscriptionEndDate,
  onPlanChange,
  showBundleDiscount,
}: Props): JSX.Element {
  const { getProductBySku } = useProductCatalogRepo({
    billingPeriod,
    currency,
  });
  const { t } = useTranslationWithPrefix('checkout:subscriptions');

  const siteUpdatedProducts = updatedProducts
    .filter((update) => update.entity.entityIdentifier === site.id)
    .map((update) => getProductBySku(update.sku));

  return (
    <S.Container>
      <S.Stack spacing="x2">
        {site.name ? (
          <S.Link kind="standalone" href={site.url} isExternal>
            {site.name}
          </S.Link>
        ) : (
          <Text>{t('site-name-is-not-available')}</Text>
        )}
        {showBundleDiscount && site.bundleDiscount && (
          <Discount discount="bundle" state={site.bundleDiscount} useDefaultColor={false} />
        )}
      </S.Stack>

      {!site.products?.length && <Text as="h3">There are no products</Text>}
      {!!site.products?.length &&
        site.products.map((product) => {
          const updatedProduct = siteUpdatedProducts.find(
            (update) =>
              update?.product.id === product?.effectiveNow?.product.id &&
              update?.allowance.sku !== product?.effectiveNow?.allowance.sku,
          );

          return (
            product !== undefined && (
              <ProductSummaryItem
                onPlanChange={({ allowance: { sku } }) => {
                  onPlanChange?.({
                    sku,
                    entity: {
                      entityIdentifier: site.id,
                      entityLabel: site.name,
                      entityType: 'site',
                    },
                  });
                }}
                currentSubscriptionPlan={product.effectiveNow}
                productInformation={updatedProduct ?? product.effectiveNow}
                futureProduct={
                  product.renewWith !== undefined && subscriptionEndDate !== undefined
                    ? {
                        effectiveDate: subscriptionEndDate,
                        product: product.renewWith,
                      }
                    : undefined
                }
                editable={editable}
                isEdited={!!updatedProduct}
                billingPeriod={billingPeriod}
                currency={currency}
                key={product.effectiveNow.product.id}
                isTwoSkusActionRequired={site.isTwoSkusActionRequired}
                subscriptionEndDate={subscriptionEndDate}
                legacyDiscount={site.legacyDiscount}
              />
            )
          );
        })}
    </S.Container>
  );
}
